import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Upload, Play, ArrowUpRight, FileText, TrendingUp, Target, Timer, Activity } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import PacingFeed from "@/components/PacingFeed";
import WeeklyWrappedCard from "@/components/WeeklyWrappedCard";

export default function Dashboard() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [e, a, t] = await Promise.all([
          api.get("/exams"),
          api.get("/attempts"),
          api.get("/analytics/history"),
        ]);
        setExams(e.data.exams || []);
        setAttempts(a.data.attempts || []);
        setTrend(t.data.trend || []);
      } catch (err) {
        console.error("dashboard load failed:", err);
      }
    })();
  }, []);

  const graded = attempts.filter((a) => a.status === "graded");
  const latest = graded[0];
  const avgAccuracy = graded.length
    ? Math.round(graded.reduce((s, a) => s + (a.analytics?.accuracy || 0), 0) / graded.length)
    : 0;
  const avgSpeed = graded.length
    ? Math.round(graded.reduce((s, a) => s + (a.analytics?.speed || 0), 0) / graded.length)
    : 0;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-8 fade-up">
        {/* Header + CTA */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Dashboard</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">Your training log</h1>
            <p className="text-stone-500 mt-2">Every attempt sharpens your edge.</p>
          </div>
          <Button
            data-testid="new-exam-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white h-11"
            onClick={() => navigate("/upload")}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload new paper
          </Button>
        </div>

        {/* Metric strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard label="Attempts" value={graded.length} icon={Activity} testid="metric-attempts" />
          <MetricCard label="Avg accuracy" value={`${avgAccuracy}%`} icon={Target} accent="emerald" testid="metric-accuracy" />
          <MetricCard label="Avg speed" value={`${avgSpeed}`} icon={Timer} accent="orange" testid="metric-speed" />
          <MetricCard
            label="Last score"
            value={latest ? `${latest.analytics?.score}/${latest.analytics?.max_score}` : "—"}
            icon={TrendingUp}
            accent="rose"
            testid="metric-last"
          />
        </div>

        {/* Performance trend */}
        {trend.length > 0 && <PacingFeed trend={trend} />}
        <WeeklyWrappedCard />

        <Card className="p-6 rounded-md border-stone-200 shadow-none">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Performance</div>
              <h2 className="font-heading text-2xl font-semibold">Score trend</h2>
            </div>
            <Link to="/history" className="text-sm text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
              Full history <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          {trend.length === 0 ? (
            <EmptyChart />
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={trend.map((t, i) => ({ ...t, idx: i + 1, pct: t.max_score ? (t.score / t.max_score) * 100 : 0 }))}>
                  <CartesianGrid stroke="#e7e5e4" strokeOpacity={0.5} vertical={false} />
                  <XAxis dataKey="idx" tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                  <YAxis tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} unit="%" />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 6, fontSize: 12 }} />
                  <Line type="monotone" dataKey="pct" stroke="#ea580c" strokeWidth={2.5} dot={{ fill: "#ea580c", r: 4 }} name="Score %" />
                  <Line type="monotone" dataKey="accuracy" stroke="#059669" strokeWidth={2} dot={{ fill: "#059669", r: 3 }} name="Accuracy %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* My exams */}
          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold">Your exams</h3>
              <Badge variant="secondary" className="font-mono text-xs">{exams.length}</Badge>
            </div>
            {exams.length === 0 ? (
              <div className="py-12 text-center border border-dashed border-stone-200 rounded-md">
                <FileText className="h-8 w-8 mx-auto text-stone-300 mb-3" />
                <p className="text-sm text-stone-500 mb-4">No exams yet. Upload your first PDF.</p>
                <Button size="sm" onClick={() => navigate("/upload")} className="bg-stone-900 hover:bg-stone-800 text-white">
                  Upload PDF
                </Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="exam-list">
                {exams.slice(0, 6).map((e) => (
                  <div key={e.exam_id} className="flex items-center justify-between p-3 rounded-md border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-sm">{e.structure?.title || e.filename}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 mt-1">
                        {e.status} · {e.structure?.questions?.length || 0} Q · {e.structure?.total_time_minutes || "—"} min
                      </div>
                    </div>
                    {e.status === "parsed" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        onClick={() => navigate(`/exam/${e.exam_id}`)}
                        data-testid={`start-exam-${e.exam_id}`}
                      >
                        <Play className="h-4 w-4 mr-1" /> Start
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/upload?exam=${e.exam_id}`)}>
                        Continue
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent attempts */}
          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold">Recent attempts</h3>
              <Badge variant="secondary" className="font-mono text-xs">{attempts.length}</Badge>
            </div>
            {attempts.length === 0 ? (
              <div className="py-12 text-center border border-dashed border-stone-200 rounded-md">
                <Activity className="h-8 w-8 mx-auto text-stone-300 mb-3" />
                <p className="text-sm text-stone-500">Attempts will appear here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attempts.slice(0, 6).map((a) => (
                  <div
                    key={a.attempt_id}
                    className="flex items-center justify-between p-3 rounded-md border border-stone-200 hover:border-orange-400 cursor-pointer transition-colors"
                    onClick={() => navigate(`/results/${a.attempt_id}`)}
                    data-testid={`attempt-${a.attempt_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-sm">{a.exam_title}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 mt-1">
                        {a.status} · {new Date(a.started_at).toLocaleDateString()}
                      </div>
                    </div>
                    {a.analytics ? (
                      <div className="text-right">
                        <div className="font-mono text-lg font-semibold tabular-nums">{a.analytics.score}</div>
                        <div className="font-mono text-[10px] text-stone-500">of {a.analytics.max_score}</div>
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-xs">Pending</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function MetricCard({ label, value, icon: Icon, accent = "stone", testid }) {
  const accentMap = {
    stone: "text-stone-900",
    emerald: "text-emerald-600",
    orange: "text-orange-600",
    rose: "text-rose-600",
  };
  return (
    <Card className="p-5 rounded-md border-stone-200 shadow-none hover:-translate-y-0.5 hover:shadow-md" data-testid={testid}
      style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}>
      <div className="flex items-start justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</div>
        <Icon className={`h-4 w-4 ${accentMap[accent]}`} />
      </div>
      <div className={`font-heading text-3xl font-bold tabular-nums ${accentMap[accent]}`}>{value}</div>
    </Card>
  );
}

function EmptyChart() {
  return (
    <div className="h-64 flex flex-col items-center justify-center border border-dashed border-stone-200 rounded-md">
      <TrendingUp className="h-10 w-10 text-stone-300 mb-3" />
      <p className="text-sm text-stone-500">Complete an exam to see your trend.</p>
    </div>
  );
}
